import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Excalidraw, exportToBlob } from '@excalidraw/excalidraw'
import '@excalidraw/excalidraw/index.css'

export type FreehandSavePayload = {
  title: string
  pngBlob: Blob
  strokesJson: string
  diagramId: string
}

export type FreehandWhiteboardHandle = {
  save: () => Promise<FreehandSavePayload>
  isDirty: () => boolean
}

type Props = {
  open: boolean
  title: string
  onTitleChange: (t: string) => void
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose: () => void
  /** Load previous scene JSON (excalidraw v2) — optional */
  initialStrokesJson?: string | null
  /** Stable key so Excalidraw remounts when opening a different scene */
  sceneKey?: string
}

type ExcalidrawApi = {
  getSceneElements: () => readonly unknown[]
  getAppState: () => Record<string, unknown> & {
    viewBackgroundColor?: string
  }
  getFiles: () => Record<string, unknown>
}

type ScenePayload = {
  v: 2
  type: 'excalidraw'
  elements: unknown[]
  appState?: { viewBackgroundColor?: string }
  files?: Record<string, unknown>
}

function createDiagramId(): string {
  return `wb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

/** Returns true if JSON is legacy freehand strokes (not editable in Excalidraw). */
export function isLegacyFreehandScene(raw?: string | null): boolean {
  if (!raw?.trim()) return false
  try {
    const data = JSON.parse(raw)
    if (data?.type === 'freehand') return true
    if (Array.isArray(data?.strokes)) return true
    if (Array.isArray(data) && data[0]?.points) return true
  } catch {
    /* ignore */
  }
  return false
}

export function isExcalidrawScene(raw?: string | null): boolean {
  if (!raw?.trim()) return false
  try {
    const data = JSON.parse(raw)
    if (data?.type === 'freehand') return false
    if (Array.isArray(data?.strokes)) return false
    if (Array.isArray(data?.elements)) return true
  } catch {
    /* ignore */
  }
  return false
}

function parseScene(raw?: string | null): {
  elements: unknown[]
  appState: Record<string, unknown>
  files?: Record<string, unknown>
} | null {
  if (!raw?.trim() || isLegacyFreehandScene(raw)) return null
  try {
    const data = JSON.parse(raw)
    if (!Array.isArray(data?.elements)) return null
    return {
      elements: data.elements,
      appState: {
        viewBackgroundColor: '#ffffff',
        ...(data.appState && typeof data.appState === 'object' ? data.appState : {}),
        collaborators: undefined,
      },
      files: data.files && typeof data.files === 'object' ? data.files : undefined,
    }
  } catch {
    return null
  }
}

const FreehandWhiteboard = forwardRef<FreehandWhiteboardHandle, Props>(
  function FreehandWhiteboard(
    {
      open,
      title,
      onTitleChange,
      onDirtyChange,
      onRequestClose,
      initialStrokesJson,
      sceneKey,
    },
    ref
  ) {
    const apiRef = useRef<ExcalidrawApi | null>(null)
    const dirtyRef = useRef(false)
    const ignoreChangeRef = useRef(true)
    const diagramIdRef = useRef(createDiagramId())
    const [mountId, setMountId] = useState(0)

    const initialData = useMemo(
      () => parseScene(initialStrokesJson),
      [mountId, sceneKey, initialStrokesJson]
    )

    const setDirty = useCallback(
      (v: boolean) => {
        dirtyRef.current = v
        onDirtyChange?.(v)
      },
      [onDirtyChange]
    )

    useEffect(() => {
      if (!open) {
        apiRef.current = null
        return
      }
      diagramIdRef.current = createDiagramId()
      dirtyRef.current = false
      ignoreChangeRef.current = true
      onDirtyChange?.(false)
      setMountId((n) => n + 1)
      const t = window.setTimeout(() => {
        ignoreChangeRef.current = false
      }, 600)
      return () => window.clearTimeout(t)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sceneKey])

    useImperativeHandle(
      ref,
      () => ({
        isDirty: () => dirtyRef.current,
        save: async () => {
          const api = apiRef.current
          if (!api) throw new Error('Bảng trắng chưa sẵn sàng')

          const elements = api.getSceneElements()
          const appState = api.getAppState()
          const files = api.getFiles()

          if (!dirtyRef.current && elements.length === 0) {
            throw new Error('Bảng trắng trống — hãy vẽ trước khi lưu')
          }

          const blob = await exportToBlob({
            elements: elements as Parameters<typeof exportToBlob>[0]['elements'],
            appState: {
              ...appState,
              exportBackground: true,
              exportWithDarkMode: false,
              viewBackgroundColor: appState.viewBackgroundColor || '#ffffff',
            },
            files: files as Parameters<typeof exportToBlob>[0]['files'],
            mimeType: 'image/png',
            quality: 0.92,
            exportPadding: 16,
          })

          const payload: ScenePayload = {
            v: 2,
            type: 'excalidraw',
            elements: [...elements],
            appState: {
              viewBackgroundColor: appState.viewBackgroundColor || '#ffffff',
            },
            files: Object.keys(files || {}).length > 0 ? files : undefined,
          }

          dirtyRef.current = false
          onDirtyChange?.(false)

          return {
            title: title.trim() || 'Bảng trắng',
            pngBlob: blob,
            strokesJson: JSON.stringify(payload),
            diagramId: diagramIdRef.current,
          }
        },
      }),
      [onDirtyChange, title]
    )

    if (!open) return null

    return (
      <div className="absolute inset-0 z-40 flex flex-col bg-slate-900/95">
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 shrink-0">
          <span className="text-sm font-semibold text-slate-800 shrink-0">Bảng trắng</span>
          <input
            type="text"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm min-w-[140px] flex-1 max-w-xs"
            placeholder="Tên bảng..."
          />
          <button
            type="button"
            onClick={onRequestClose}
            className="ml-auto px-3 py-1.5 text-sm rounded-md border border-slate-300 bg-white hover:bg-slate-50 font-medium"
          >
            Đóng
          </button>
        </div>

        <div className="flex-1 min-h-0 relative bg-white [&_.excalidraw]:h-full [&_.excalidraw]:w-full">
          <Excalidraw
            key={`excalidraw-${sceneKey || 'new'}-${mountId}`}
            langCode="vi-VN"
            theme="light"
            excalidrawAPI={(api) => {
              apiRef.current = api as unknown as ExcalidrawApi
            }}
            initialData={
              initialData
                ? {
                    elements: initialData.elements as any,
                    appState: initialData.appState as any,
                    files: initialData.files as any,
                    scrollToContent: true,
                  }
                : {
                    appState: { viewBackgroundColor: '#ffffff' },
                  }
            }
            UIOptions={{
              canvasActions: {
                changeViewBackgroundColor: true,
                clearCanvas: true,
                export: false,
                loadScene: false,
                saveToActiveFile: false,
                toggleTheme: false,
                saveAsImage: false,
              },
            }}
            onChange={() => {
              if (ignoreChangeRef.current) return
              if (!dirtyRef.current) setDirty(true)
            }}
          />
        </div>
        <p className="text-xs text-slate-400 px-3 py-1.5 bg-slate-900 shrink-0">
          Bút tay · shapes · chữ · màu (Excalidraw) · Đóng sẽ hỏi Lưu hoặc Không lưu
        </p>
      </div>
    )
  }
)

export default FreehandWhiteboard
